<script setup>
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import VendorAuthLayout from '@/Layouts/VendorAuthLayout.vue'

defineOptions({ layout: VendorAuthLayout })

const { t } = useI18n()

const props = defineProps({
  options:    { type: Array,  default: () => [] },
  state:      { type: String, default: 'ready' }, // 'ready' | 'needs_bot_message' | 'no_channels'
  identifier: { type: String, default: '' },
  purpose:    { type: String, default: 'login' }
})

const form = useForm({ channel: '' })

function pick(channel) {
  if (!channel.enabled) return
  form.channel = channel.channel
  form.post('/otp/dispatch', { preserveScroll: true })
}
</script>

<template>
  <h1 class="picker-title">{{ t('vendor_portal.otp.picker.title') }}</h1>
  <p class="picker-sub">{{ t('vendor_portal.otp.picker.sub') }}</p>

  <!-- :no_channels — full dead-end panel with back-to-login -->
  <div v-if="state === 'no_channels'" class="panel panel--info">
    <p class="panel__title">{{ t('vendor_portal.otp.picker.no_channels.title') }}</p>
    <p class="panel__body">{{ t('vendor_portal.otp.picker.no_channels.body') }}</p>
    <a href="/login" class="panel__cta">{{ t('vendor_portal.otp.picker.no_channels.back_to_login') }}</a>
  </div>

  <template v-else>
    <!-- :needs_bot_message — advisory banner above the (clickable) channel list -->
    <div v-if="state === 'needs_bot_message'" class="panel panel--warn">
      <p class="panel__title">{{ t('vendor_portal.otp.picker.needs_bot.title') }}</p>
      <p class="panel__body">{{ t('vendor_portal.otp.picker.needs_bot.body') }}</p>
    </div>

    <ul class="picker-list">
      <li v-for="opt in options" :key="opt.channel + opt.label">
        <button
          :class="['picker-row', !opt.enabled && 'is-disabled']"
          :disabled="!opt.enabled"
          @click="pick(opt)"
        >
          <span class="picker-row__name">{{ opt.label }}</span>
          <span v-if="!opt.enabled" class="picker-row__badge">
            {{ t('vendor_portal.otp.picker.needs_bot.badge') }}
          </span>
        </button>
      </li>
    </ul>

    <a href="/otp/verify" class="picker-link">{{ t('vendor_portal.otp.picker.already') }}</a>
    <a href="/login" class="picker-link picker-link--muted">{{ t('vendor_portal.otp.picker.use_password') }}</a>
  </template>
</template>

<style scoped>
.picker-title { font-size: 1.25rem; font-weight: 700; color: var(--ink); margin: 0 0 0.5rem; }
.picker-sub   { color: var(--inkSoft); margin: 0 0 1rem; font-size: 0.9375rem; }
.picker-list  { list-style: none; padding: 0; margin: 0 0 1rem; display: grid; gap: 0.5rem; }
.picker-row {
  width: 100%; display: flex; justify-content: space-between; align-items: center;
  padding: 0.65rem 0.9rem; background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-button, 8px); color: var(--ink); font-size: 0.9375rem;
  cursor: pointer; text-align: left;
}
.picker-row:hover:not(.is-disabled) { border-color: var(--accent); }
.picker-row.is-disabled { color: var(--muted); cursor: not-allowed; }
.picker-row__badge {
  font-size: 0.6875rem; padding: 0.15rem 0.4rem;
  background: var(--surfaceAlt); border-radius: 999px; color: var(--muted);
}
.picker-link  { color: var(--accent); text-decoration: none; font-size: 0.875rem; text-align: center; display: block; margin-top: 0.5rem; }
.picker-link--muted { color: var(--muted); }
.picker-link--muted:hover { color: var(--accent); }
.panel {
  padding: 0.875rem 1rem; border-radius: var(--radius-button, 8px);
  margin-bottom: 1rem; border: 1px solid var(--line); background: var(--surface);
}
.panel--info  { border-color: color-mix(in oklch, var(--accent) 40%, var(--line)); background: color-mix(in oklch, var(--accent) 5%, var(--surface)); }
.panel--warn  { border-color: color-mix(in oklch, var(--warn) 40%, var(--line)); background: color-mix(in oklch, var(--warn) 8%, var(--surface)); }
.panel__title { margin: 0 0 0.25rem; font-size: 0.9375rem; font-weight: 600; color: var(--ink); }
.panel__body  { margin: 0; color: var(--inkSoft); font-size: 0.875rem; line-height: 1.5; }
.panel__cta {
  display: inline-block; margin-top: 0.75rem;
  padding: 0.5rem 0.9rem; background: var(--accent); color: var(--accentInk);
  border-radius: var(--radius-button, 8px); text-decoration: none; font-size: 0.875rem; font-weight: 600;
}
</style>
